import { useMemo, useRef, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ModalActionButton } from 'interfaces/components';
import { AccessElements, StatusCode } from 'helpers/app.config';
import { useGlobalContext } from 'context/GlobalContext';
import ExportPDFZipButton from 'pages/Ticket/Detail/components/DetailTab/components/exportPDFZip';
import { TicketSearched } from 'api/ticket';

import {
  columnVisibilityModel,
  programmedColumnVisibilityModel
} from '../TicketsDataGrid/columns';

import './index.css';
import useGetTicketStatusCategory from 'pages/Ticket/Find/hooks/useGetTicketStatusCategory';

export enum OrganizeStatusCode {
  ClientPendings = -7,
  InternalEarrings = -6,
  AllStates = -5,
  Revision = -4,
  Finished = -3,
  Custom = -2,
  Active = -1,
  Cancelled = StatusCode.Cancelled,
  Historic = StatusCode.Historic,
  Programmed = 5,
  Pending = StatusCode.Pending
}

export const statusOptions = [
  {
    id: OrganizeStatusCode.AllStates,
    name: 'ticket.statusOption.all',
    disabled: false,
    permission: [] as AccessElements[]
  },
  {
    id: OrganizeStatusCode.Custom,
    name: 'ticket.statusOption.personalized',
    disabled: true,
    permission: [] as AccessElements[]
  },
  {
    id: OrganizeStatusCode.Active,
    name: 'ticket.statusOption.actives',
    disabled: false,
    permission: [] as AccessElements[]
  },
  {
    id: OrganizeStatusCode.InternalEarrings,
    name: 'ticket.statusOption.pendingInternal',
    disabled: false,
    permission: [] as AccessElements[]
  },
  {
    id: OrganizeStatusCode.ClientPendings,
    name: 'ticket.statusOption.pendingForClient',
    disabled: false,
    permission: [] as AccessElements[]
  },
  {
    id: OrganizeStatusCode.Revision,
    name: 'ticket.statusOption.revision',
    disabled: false,
    permission: [AccessElements.SearchRevisionReason]
  },
  {
    id: OrganizeStatusCode.Programmed,
    name: 'ticket.statusOption.programmed',
    disabled: false,
    permission: [] as AccessElements[]
  },
  {
    id: OrganizeStatusCode.Finished,
    name: 'ticket.statusOption.finished',
    disabled: false,
    permission: [AccessElements.SearchFinalizedReason] as AccessElements[]
  }
];

type Props = {
  title?: string;
  actions?: ModalActionButton[];
  ticketStatus?: OrganizeStatusCode;
  onChangeOrganizeStatus?: (statusId: OrganizeStatusCode) => void;
  openChangeEngineerModal?: () => void;
  hideTaskForceBtn?: boolean;
  disableTaskForceBtn?: boolean;
  disableStatusBtn?: boolean;
  setReturnColumns?: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
  returnColumns?: GridColumnVisibilityModel;
  handleResetItemsForExport?: () => void;
  tickets: TicketSearched[];
};

function TicketsDataGridHeader({
  title = '',
  actions = [],
  ticketStatus = 0,
  onChangeOrganizeStatus,
  openChangeEngineerModal,
  hideTaskForceBtn = false,
  disableTaskForceBtn = false,
  disableStatusBtn = true,
  setReturnColumns,
  returnColumns,
  handleResetItemsForExport,
  tickets
}: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [hideStatusBtn, setHideStatusBtn] = useState<boolean>(false);
  const [savedReturnColumns, setSavedReturnColumns] =
    useState<GridColumnVisibilityModel>(columnVisibilityModel);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { hasPerm } = useGlobalContext();
  const { t } = useTranslation('global');

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const ticketStatusCategories = useGetTicketStatusCategory();

  const handleChangeStatus = (event: SelectChangeEvent<number>) => {
    const statusCode = Number(event?.target?.value);

    handleResetItemsForExport();

    if (onChangeOrganizeStatus) onChangeOrganizeStatus(statusCode);

    if (statusCode !== OrganizeStatusCode.Programmed) {
      setSavedReturnColumns(returnColumns);
    }

    if (statusCode === OrganizeStatusCode.Programmed) {
      setReturnColumns(programmedColumnVisibilityModel);
    } else if (
      JSON.stringify(Object.keys(returnColumns)) ===
      JSON.stringify(Object.keys(programmedColumnVisibilityModel))
    ) {
      setReturnColumns(savedReturnColumns);
    }

    setHideStatusBtn(
      statusCode === OrganizeStatusCode.Historic ||
        statusCode === OrganizeStatusCode.Cancelled
    );
  };

  const selectOptions = useMemo(
    () => [
      ...(ticketStatusCategories?.data?.map((status) => ({
        categoryId: status.categoryId,
        categoryName: status.categoryName,
        disabled: false
      })) || []),
      {
        categoryId: OrganizeStatusCode.Custom,
        categoryName: 'ticket.statusOption.personalized',
        disabled: true
      }
    ],
    [ticketStatusCategories?.data]
  );

  return (
    <Box
      alignItems="center"
      className={`TicketsDataGridHeaderResponsive ${
        hideTaskForceBtn ? 'TaskForceBtnResponsive' : ''
      }`}
      display="flex"
      justifyContent="space-between"
      marginBottom={3}
      paddingRight="50px"
      width="100%"
    >
      <Box>
        <Typography variant="h5">{title}</Typography>
        <Stack alignItems="center" direction="row" gap={3} width="400px">
          <Typography>{t('ticket.messages.organizedBy')}:</Typography>
          <Select
            onChange={handleChangeStatus}
            size="small"
            sx={{ width: '175px', marginTop: '2px' }}
            value={ticketStatus}
          >
            {selectOptions.map((status) => (
              <MenuItem
                key={status.categoryId}
                sx={{
                  display: status.disabled && 'none'
                }}
                value={status.categoryId}
              >
                {t(status.categoryName)}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Box>

      <Box
        className={`${hideTaskForceBtn ? 'TaskForceBoxBtnResponsive' : ''}`}
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: '.6rem'
        }}
      >
        {hasPerm([
          AccessElements.LocationSearch,
          AccessElements.LocationCreate
        ]) && (
          <Button
            onClick={() => navigate('/ubications')}
            sx={{
              backgroundColor: '#205691'
            }}
            type="button"
            variant="contained"
          >
            {t('drawer.ubications.ubicationsTitle')}
          </Button>
        )}
        {hideTaskForceBtn && (
          <Button
            disabled={disableTaskForceBtn}
            onClick={openChangeEngineerModal}
            variant="contained"
          >
            {t('ticket.messages.changeTaskForce')}
          </Button>
        )}
        {!hideStatusBtn && (
          <>
            <ExportPDFZipButton ticketsData={tickets} />
            {/* Se oculto este boton el dia 19/08 para la liberacion de la F4 */}
            {/* Hay que mejorar el tema de la descarga con el chico de BI */}
            {/* <ExportDetailsButton ticketsData={tickets} /> */}
            <Button
              disabled={disableStatusBtn}
              endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
              onClick={handleToggle}
              ref={anchorRef}
              variant="contained"
            >
              {t('ticket.messages.updateStatus')}
            </Button>
            <Popper
              anchorEl={anchorRef.current}
              open={open}
              style={{ zIndex: '1201' }}
              transition
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList autoFocusItem>
                        {actions.map(({ handle, text, disabled }) => (
                          <MenuItem
                            disabled={disabled}
                            key={text}
                            onClick={handle}
                          >
                            {text}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </>
        )}
      </Box>
    </Box>
  );
}

export default TicketsDataGridHeader;
